<template>
  <div
    id="features"
    class="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8"
  >
    <div
      v-if="!featuresOnly"
      :class="{ 'mb-10 md:mb-12': !featuresOnly }"
      class="max-w-xl md:mx-auto sm:text-center lg:max-w-2xl"
    >
      <div>
        <p
          class="inline-block px-3 py-px mb-4 text-xs font-semibold tracking-wider text-nt-blue uppercase rounded-full bg-nt-blue-lighter"
        >
          Core Features 100% Free
        </p>
      </div>
      <h2
        class="max-w-lg mb-6 font-sans text-3xl font-bold leading-none tracking-tight text-gray-900 dark:text-gray-100 sm:text-4xl md:mx-auto"
      >
        <span class="relative inline-block">
          <svg
            viewBox="0 0 52 24"
            fill="currentColor"
            class="text-nt-blue-light absolute top-0 left-0 z-0 hidden w-32 -mt-8 -ml-20 text-blue-gray-100 lg:w-32 lg:-ml-28 lg:-mt-10 sm:block"
          >
            <defs>
              <pattern
                id="27df4f81-c854-45de-942a-fe90f7a300f9"
                x="0"
                y="0"
                width=".135"
                height=".30"
              >
                <circle
                  cx="1"
                  cy="1"
                  r=".7"
                />
              </pattern>
            </defs>
            <rect
              fill="url(#27df4f81-c854-45de-942a-fe90f7a300f9)"
              width="52"
              height="24"
            />
          </svg>
          <span class="relative">The</span>
        </span>
        easiest way to create forms for
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="12 0.19 487.619 510.941"
          class="w-5 h-5 sm:w-8 sm:h-8 lg:w-9 lg:h-9 inline filter dark:invert -mt-2"
        >
          <path
            d="M96.085 91.118c15.81 12.845 21.741 11.865 51.43 9.884l279.888-16.806c5.936 0 1-5.922-.98-6.906L379.94 43.686c-8.907-6.915-20.773-14.834-43.516-12.853L65.408 50.6c-9.884.98-11.858 5.922-7.922 9.883zm16.804 65.228v294.491c0 15.827 7.909 21.748 25.71 20.769l307.597-17.799c17.81-.979 19.794-11.865 19.794-24.722V136.57c0-12.836-4.938-19.758-15.84-18.77l-321.442 18.77c-11.863.997-15.82 6.931-15.82 19.776zm303.659 15.797c1.972 8.903 0 17.798-8.92 18.799l-14.82 2.953v217.412c-12.868 6.916-24.734 10.87-34.622 10.87-15.831 0-19.796-4.945-31.654-19.76l-96.944-152.19v147.248l30.677 6.922s0 17.78-24.75 17.78l-68.23 3.958c-1.982-3.958 0-13.832 6.921-15.81l17.805-4.935V210.7l-24.721-1.981c-1.983-8.903 2.955-21.74 16.812-22.736l73.195-4.934L358.186 335.22V198.836l-25.723-2.952c-1.974-10.884 5.927-18.787 15.819-19.767zM42.653 23.919l281.9-20.76c34.618-2.969 43.525-.98 65.283 14.825l89.986 63.247c14.848 10.876 19.797 13.837 19.797 25.693v346.883c0 21.74-7.92 34.597-35.608 36.564L136.64 510.14c-20.785.991-30.677-1.971-41.562-15.815l-66.267-85.978C16.938 392.52 12 380.68 12 366.828V58.495c0-17.778 7.922-32.608 30.653-34.576z"
            fill-rule="evenodd"
          />
        </svg>
        Notion Users
      </h2>
      <p class="text-base text-gray-700 dark:text-gray-300 md:text-lg">
        AirFormTable was created to help Notion users to achieve more with their
        favourite tool. Need a contact form? Doing a survey? Create a form in 3
        minutes and receive responses directly in Notion.
      </p>
    </div>
    <div class="grid max-w-screen-lg gap-8 row-gap-10 mx-auto md:grid-cols-2">
      <div class="flex flex-col max-w-md sm:mx-auto sm:flex-row">
        <div class="mr-4">
          <div
            class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-nt-blue-lighter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 text-nt-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>
        <div>
          <h6 class="mb-3 text-xl font-bold leading-5">
            Infinite Number of Fields
          </h6>
          <p class="mb-3 text-sm dark:text-gray-100 text-gray-900">
            There are no limits on the number of input fields in your forms.
            Create as many as your Notion database has if you want to. Organize
            fields and decide which are required.
          </p>
        </div>
      </div>
      <div class="flex flex-col max-w-md sm:mx-auto sm:flex-row">
        <div class="mr-4">
          <div
            class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-nt-blue-lighter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 text-nt-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
        </div>
        <div>
          <h6 class="mb-3 text-xl font-bold leading-5">
            Infinite Number of Forms
          </h6>
          <p class="mb-3 text-sm dark:text-gray-100 text-gray-900">
            You can create as many forms as you need. You can even create
            multiple forms for the same Notion database, with more or less
            fields.
          </p>
        </div>
      </div>
      <div class="flex flex-col max-w-md sm:mx-auto sm:flex-row">
        <div class="mr-4">
          <div
            class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-nt-blue-lighter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 text-nt-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
        <div>
          <h6 class="mb-3 text-xl font-bold leading-5">
            Infinite Responses
          </h6>
          <p class="mb-3 text-sm dark:text-gray-100 text-gray-900">
            All of you forms can have unlimited responses, no need to worry
            about quotas and other stressful metrics.
          </p>
        </div>
      </div>
      <div class="flex flex-col max-w-md sm:mx-auto sm:flex-row">
        <div class="mr-4">
          <div
            class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-nt-blue-lighter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 text-nt-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
          </div>
        </div>
        <div>
          <h6 class="mb-3 text-xl font-bold leading-5">
            Notifications
          </h6>
          <p class="mb-3 text-sm dark:text-gray-100 text-gray-900">
            Receive notifications directly in your mailbox whenever your from
            has a new submission (if you want to).
          </p>
        </div>
      </div>
      <div class="flex flex-col max-w-md sm:mx-auto sm:flex-row">
        <div class="mr-4">
          <div
            class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-nt-blue-lighter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 text-nt-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
              />
            </svg>
          </div>
        </div>
        <div>
          <h6 class="mb-3 text-xl font-bold leading-5">
            Integrate Anywhere
          </h6>
          <p class="mb-3 text-sm dark:text-gray-100 text-gray-900">
            You can integrate your form anywhere: on your website or even
            directly within a Notion Page.
          </p>
        </div>
      </div>
      <div class="flex flex-col max-w-md sm:mx-auto sm:flex-row">
        <div class="mr-4">
          <div
            class="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-nt-blue-lighter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6 text-nt-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          </div>
        </div>
        <div>
          <h6 class="mb-3 text-xl font-bold leading-5">
            Dark Mode
          </h6>
          <p class="mb-3 text-sm dark:text-gray-100 text-gray-900">
            A beautiful dark mode. Integrate your form to your Notion page, and
            it will look beautiful. Even if you're using the dark mode.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    featuresOnly: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({}),

  methods: {},
}
</script>
